import { Check, ScanFace } from 'lucide-react';

import { PreviewHeader } from '../PreviewHeader';

// Our own wording, not the designer's. Theirs is placeholder copy and one line even
// tells a Face Match applicant to "keep the entire PAN card inside the frame", which
// belongs to a different step entirely. Kept to four short lines — an applicant
// standing with their phone up reads two at most.
const INSTRUCTIONS = [
  'Find a well-lit spot and face the light.',
  'Remove glasses, masks or anything covering your face.',
  'Hold the phone at eye level, an arm’s length away.',
  'Keep still while the photo is taken.',
];

// Face Match — the applicant captures ONE image: a LIVE camera selfie (source_image).
// The reference (target_image) is supplied server-side from an earlier Aadhaar step
// and is intentionally NOT shown here. OWN interim design (swap for the designer's
// when it arrives). `selfie` lets the session runner inject the live camera / captured
// still; the builder shows a placeholder.
/**
 * Screen 1 of 2 — what to do BEFORE the camera opens.
 *
 * The designer's flow is two screens (prepare → capture) and ours collapsed both into
 * one, which meant the instructions competed with the live camera for the same space.
 * Splitting them means the applicant reads the rules first, then sees nothing but
 * their own face while the photo is taken.
 */
export function FaceMatchPrepareScreen({
  title = 'Face Verification',
  subtitle = 'A quick selfie to confirm it’s you',
}: {
  title?: string;
  subtitle?: string;
} = {}) {
  return (
    <>
      <PreviewHeader title={title} subtitle={subtitle} />

      <div className="mb-4 flex w-full items-center justify-center rounded-2xl bg-[#EEF3FF] py-8">
        <div className="flex h-[132px] w-[132px] items-center justify-center rounded-full border-[3px] border-[#99B6FF] bg-white">
          <ScanFace size={64} strokeWidth={1.1} className="text-[#0048FF]/55" />
        </div>
      </div>

      <ul className="flex flex-col gap-2.5">
        {INSTRUCTIONS.map((text) => (
          <li
            key={text}
            className="flex items-start gap-2 text-[length:var(--text-12)] leading-tight text-gray30"
          >
            <div className="flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full bg-primary text-white">
              <Check size={10} strokeWidth={4} />
            </div>
            <span>{text}</span>
          </li>
        ))}
      </ul>

      <p className="mb-3 mt-2 text-[11px] leading-snug text-[#6B7280]">
        Your selfie is used only to verify your identity. We do not store your personal information
        beyond what is required by law.
      </p>
    </>
  );
}

/**
 * Screen 2 of 2 — the live capture.
 *
 * Now the designer's circular gradient ring with the REAL camera in it
 * ({@link SelfieCaptureScreen}), replacing the blue oval with a placeholder icon we
 * had drawn ourselves while waiting for their design.
 */
export { SelfieCaptureScreen as FaceMatchScreen } from './SelfieCaptureScreen';
